import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Stack,
  Divider,
  Box,
  InputAdornment,
  useMediaQuery,
  TextField,
} from '@mui/material';

import { DialogCloseButton } from '../../components/core/DialogCloseButton';
import { Button } from '../../components/core/Button/Button';
import { useMerchant } from '../../contexts/MerchantContext';
import { CreateMerchantResponse } from '../../store/modules/merchant/types';
import CopyButton from '../../components/core/CopyButton';
import appTheme from '../../styles/themes/theme';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';

type Props = {
  createdMerchant: Pick<CreateMerchantResponse, 'merchantId' | 'name' | 'apiKeys'>;
  parentMerchantName?: string;
  open: boolean;
  onClose: () => void;
};

const DetailLine = ({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children?: React.ReactNode;
}) => (
  <Box sx={{ flex: 1 }}>
    <Typography sx={{ fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    {description && <Typography>{description}</Typography>}
    {children}
  </Box>
);

export const MerchantCreatedDialog = ({
  createdMerchant,
  parentMerchantName,
  open,
  onClose,
}: Props) => {
  const { setMerchant, getHomePath } = useMerchant();
  const navigate = useMerchantAwareNavigate();
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));
  const apiKey = createdMerchant.apiKeys[0]?.apiKey;

  const handleGoToDashboard = () => {
    onClose();
    navigate(getHomePath());
  };

  const handleContinue = () => {
    onClose();
    setMerchant(createdMerchant.merchantId);
    navigate(`${getHomePath()}?merchantId=${createdMerchant.merchantId}`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 600 }}>Merchant Created</DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        <Typography sx={{ mb: 2 }}>The merchant account has been created successfully!</Typography>
        <Stack
          sx={{
            backgroundColor: 'grey.100',
            border: '1px solid',
            borderColor: 'grey.300',
            p: { xs: 2, sm: 3 },
            borderRadius: '8px',
          }}
          spacing={4}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 4, sm: 2 },
            }}
          >
            <DetailLine label="Name" description={createdMerchant.name} />
            {parentMerchantName ? (
              <DetailLine label="Parent Merchant" description={parentMerchantName} />
            ) : null}
          </Box>
          <DetailLine label="Merchant ID">
            <TextField
              value={createdMerchant.merchantId}
              sx={{ fontFamily: 'monospace', backgroundColor: 'white', mt: 1 }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyButton copyString={createdMerchant.merchantId} title="Merchant ID" />
                  </InputAdornment>
                ),
              }}
            />
          </DetailLine>
          {apiKey ? (
            <DetailLine label="API Key">
              <Stack spacing={1} sx={{ mt: 1 }}>
                <TextField
                  value={apiKey}
                  sx={{ fontFamily: 'monospace', backgroundColor: 'white', mt: 1 }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyButton copyString={apiKey} title="API Key" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography sx={{ fontSize: '12px', px: 1 }}>
                  Please copy your new API key and store it someplace safe. You will not be able to
                  access it again if it is lost, and it cannot be regained after this window is
                  closed.
                </Typography>
              </Stack>
            </DetailLine>
          ) : null}
        </Stack>
        <Divider sx={{ mt: 4, mb: 2 }} />
        <Stack direction="row" spacing={1}>
          <Button
            text={isMobile ? 'Go to Home' : 'Go to Home Page'}
            theme="secondary"
            onClick={handleGoToDashboard}
            isFullWidth
          />
          <Button text="View Merchant" theme="primary" onClick={handleContinue} isFullWidth />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
